import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Star, Send, Loader2, Camera, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';

// Sub-rating categories - ALL REQUIRED
const SUB_RATINGS = [
  { key: 'rating_quality', label: 'Qualità del prodotto', description: 'Il prodotto corrisponde alle aspettative?' },
  { key: 'rating_communication', label: 'Comunicazione', description: 'Rapidità e chiarezza nelle risposte' },
  { key: 'rating_shipping', label: 'Tempi di consegna', description: 'Rispetto dei tempi promessi' },
  { key: 'rating_packaging', label: 'Imballaggio', description: 'Cura nella confezione e protezione' },
  { key: 'rating_value', label: 'Rapporto qualità/prezzo', description: 'Valore percepito rispetto al costo' },
];

export default function ReviewDialog({ 
  open, 
  onOpenChange, 
  order,
  product, // Optional: specific product to review (for multi-product orders)
  onReviewSubmitted 
}) {
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Sub-ratings state - ALL preset to 5 stars
  const [subRatings, setSubRatings] = useState({
    rating_quality: 5,
    rating_communication: 5,
    rating_shipping: 5,
    rating_packaging: 5,
    rating_value: 5,
  });
  const [subHoverRatings, setSubHoverRatings] = useState({});
  
  // Images state
  const [images, setImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const fileInputRef = useRef(null);

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Max 5 images total
    if (images.length + files.length > 5) {
      toast.error('Puoi caricare al massimo 5 immagini');
      return;
    }
    
    setUploadingImages(true);
    const uploadedUrls = [];
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    
    try {
      for (const file of files) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} non è un'immagine valida`);
          continue;
        }
        
        // Max 5MB per image
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`${file.name} supera i 5MB`);
          continue;
        }
        
        // Upload via backend API (bypasses Supabase RLS issues)
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await fetch(`${API_URL}/api/upload/review-image`, {
          method: 'POST',
          body: formData,
        });
        
        if (!response.ok) {
          const error = await response.json();
          toast.error(`Errore upload ${file.name}: ${error.detail || 'Errore sconosciuto'}`);
          continue;
        }
        
        const data = await response.json();
        uploadedUrls.push(data.url);
      }
      
      if (uploadedUrls.length > 0) {
        setImages(prev => [...prev, ...uploadedUrls]);
        toast.success(`${uploadedUrls.length} immagine/i caricate`);
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      toast.error('Errore nel caricamento delle immagini');
    } finally {
      setUploadingImages(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (rating < 1) {
      toast.error('Seleziona almeno una stella');
      return;
    }

    setLoading(true);
    try {
      // Get crafter ID and product ID
      let crafterId = order.crafter_id;
      let productId = null;
      let productTitle = order.title;

      // If a specific product is passed (multi-product order)
      if (product) {
        crafterId = product.crafter?.id || product.crafter_id || order.crafter_id;
        productId = product.productId || product.id;
        productTitle = product.title;
      }
      // For cart orders without specific product, get first item
      else if (order.order_type === 'cart' || order.items) {
        const firstItem = order.items?.[0] || order.crafterItems?.[0];
        crafterId = firstItem?.crafter?.id || order.crafter_id;
        productId = firstItem?.productId;
        productTitle = firstItem?.title || order.title;
      }

      if (!crafterId) {
        toast.error('Impossibile identificare l\'artigiano');
        setLoading(false);
        return;
      }

      // Build review data - ALL sub-ratings included (required)
      const reviewData = {
        order_id: order.id,
        product_id: productId,
        crafter_id: crafterId,
        client_id: order.client_id,
        rating,
        title: title.trim() || null,
        comment: comment.trim() || null,
        is_verified_purchase: true,
        images: images.length > 0 ? images : [],
        // Sub-ratings - ALL required
        rating_quality: subRatings.rating_quality,
        rating_communication: subRatings.rating_communication,
        rating_shipping: subRatings.rating_shipping,
        rating_packaging: subRatings.rating_packaging,
        rating_value: subRatings.rating_value,
      };

      // Create review
      const { error: reviewError } = await supabase
        .from('reviews')
        .insert(reviewData);

      if (reviewError) throw reviewError;

      // For single-product orders or request orders, mark order as reviewed
      // For multi-product cart orders, check if all products are now reviewed
      if (!product && (!order.items || order.items.length <= 1)) {
        await supabase
          .from('orders')
          .update({ has_review: true })
          .eq('id', order.id);
      } else if (product && order.items) {
        // Check if all items in this order now have reviews
        const { data: existingReviews } = await supabase
          .from('reviews')
          .select('product_id')
          .eq('order_id', order.id);
        
        const reviewedProductIds = new Set(existingReviews?.map(r => r.product_id) || []);
        reviewedProductIds.add(productId); // Add current product
        
        const allProductIds = order.items.map(item => item.productId);
        const allReviewed = allProductIds.every(pid => reviewedProductIds.has(pid));
        
        if (allReviewed) {
          await supabase
            .from('orders')
            .update({ has_review: true })
            .eq('id', order.id);
        }
      }

      toast.success('Grazie per la tua recensione!');
      onOpenChange(false);
      if (onReviewSubmitted) onReviewSubmitted();
      
      // Reset form
      setRating(5);
      setTitle('');
      setComment('');
      setImages([]);
      setSubRatings({
        rating_quality: 5,
        rating_communication: 5,
        rating_shipping: 5,
        rating_packaging: 5,
        rating_value: 5,
      });
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error('Errore nell\'invio della recensione');
    } finally {
      setLoading(false);
    }
  };

  const StarButton = ({ index, currentRating, hoverValue, onRate, onHover, onLeave, size = 'lg' }) => {
    const filled = (hoverValue || currentRating) >= index;
    const starClass = size === 'lg' ? 'w-8 h-8' : 'w-5 h-5';
    const padding = size === 'lg' ? 'p-1' : 'p-0.5';
    
    return (
      <button
        type="button"
        onClick={() => onRate(index)}
        onMouseEnter={() => onHover(index)}
        onMouseLeave={onLeave}
        className={`${padding} transition-transform hover:scale-110`}
      >
        <Star 
          className={`${starClass} transition-colors ${
            filled 
              ? 'fill-amber-400 text-amber-400' 
              : 'text-slate-300'
          }`}
        />
      </button>
    );
  };

  const getRatingLabel = (r) => {
    switch(r) {
      case 1: return 'Pessimo';
      case 2: return 'Scarso';
      case 3: return 'Sufficiente';
      case 4: return 'Buono';
      case 5: return 'Eccellente';
      default: return '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-500" />
            Lascia una recensione
          </DialogTitle>
          <DialogDescription>
            Condividi la tua esperienza con questo ordine
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Order/Product Info */}
          <div className="bg-slate-50 rounded-lg p-3">
            {product ? (
              // Specific product review
              <div className="flex items-center gap-3">
                {product.image && (
                  <img 
                    src={product.image} 
                    alt={product.title}
                    className="w-12 h-12 rounded-lg object-cover"
                  />
                )}
                <div>
                  <p className="font-medium text-sm">{product.title}</p>
                  <p className="text-xs text-slate-500">
                    Ordine del {new Date(order?.created_at).toLocaleDateString('it-IT')}
                  </p>
                </div>
              </div>
            ) : (
              // General order review
              <>
                <p className="font-medium text-sm">{order?.title}</p>
                <p className="text-xs text-slate-500">
                  Ordine del {new Date(order?.created_at).toLocaleDateString('it-IT')}
                </p>
              </>
            )}
          </div>

          {/* Overall Star Rating */}
          <div className="text-center">
            <Label className="text-sm font-medium mb-3 block">
              Valutazione complessiva
            </Label>
            <div className="flex justify-center gap-1 mb-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <StarButton 
                  key={i} 
                  index={i} 
                  currentRating={rating}
                  hoverValue={hoverRating}
                  onRate={setRating}
                  onHover={setHoverRating}
                  onLeave={() => setHoverRating(0)}
                  size="lg"
                />
              ))}
            </div>
            <p className="text-sm font-medium text-amber-600">
              {getRatingLabel(hoverRating || rating)}
            </p>
          </div>

          {/* Sub-ratings - ALWAYS VISIBLE, ALL REQUIRED */}
          <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
            <p className="text-sm font-medium text-slate-700 mb-2">Valutazioni dettagliate</p>
            {SUB_RATINGS.map(({ key, label, description }) => (
              <div key={key} className="flex items-center justify-between">
                <div className="flex-1 pr-4">
                  <p className="text-sm font-medium text-slate-700">{label}</p>
                  <p className="text-xs text-slate-500">{description}</p>
                </div>
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <StarButton
                      key={i}
                      index={i}
                      currentRating={subRatings[key]}
                      hoverValue={subHoverRatings[key] || 0}
                      onRate={(val) => setSubRatings(prev => ({ ...prev, [key]: val }))}
                      onHover={(val) => setSubHoverRatings(prev => ({ ...prev, [key]: val }))}
                      onLeave={() => setSubHoverRatings(prev => ({ ...prev, [key]: 0 }))}
                      size="sm"
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Title */}
          <div>
            <Label htmlFor="review-title" className="text-sm font-medium">
              Titolo (opzionale)
            </Label>
            <Input
              id="review-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Riassumi la tua esperienza"
              className="mt-1"
              maxLength={100}
            />
          </div>

          {/* Comment */}
          <div>
            <Label htmlFor="review-comment" className="text-sm font-medium">
              La tua recensione (opzionale)
            </Label>
            <Textarea
              id="review-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Racconta la tua esperienza: qualità del prodotto, comunicazione con l'artigiano, tempi di consegna..."
              className="mt-1"
              rows={4}
              maxLength={1000}
            />
            <p className="text-xs text-slate-400 mt-1 text-right">
              {comment.length}/1000
            </p>
          </div>

          {/* Image Upload */}
          <div>
            <Label className="text-sm font-medium mb-2 block">
              Aggiungi foto del prodotto (opzionale)
            </Label>
            
            {/* Image Preview Grid */}
            {images.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {images.map((url, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={url}
                      alt={`Foto ${index + 1}`}
                      className="w-20 h-20 object-cover rounded-lg border border-slate-200"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Upload Button */}
            {images.length < 5 && (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                  id="review-images"
                />
                <label
                  htmlFor="review-images"
                  className={`flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-[#008080] hover:bg-slate-50 transition-colors ${uploadingImages ? 'opacity-50 pointer-events-none' : ''}`}
                >
                  {uploadingImages ? (
                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                  ) : (
                    <Camera className="w-5 h-5 text-slate-400" />
                  )}
                  <span className="text-sm text-slate-500">
                    {uploadingImages ? 'Caricamento...' : `Aggiungi foto (${images.length}/5)`}
                  </span>
                </label>
              </div>
            )}
            <p className="text-xs text-slate-400 mt-1">
              Max 5 immagini, 5MB ciascuna
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Annulla
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={loading || uploadingImages}
            className="bg-[#008080] text-white"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Invio...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Invia recensione
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
